import { IHSL, IRGB } from '@fluentui/react';
import { IRGBToHEX, RGBToHSL } from './colorHelpers';

export type ImageAccentResult = {
  accentRgb: IRGB;
  accentHex: string;
  accentHsl: IHSL;
  gradientCss: string;
};

export type ProminentImageColorOptions = {
  /** Downscale width for faster processing (keeps aspect ratio) */
  maxWidth?: number;
  /** Sample stride in pixels; higher = faster, less accurate */
  sampleStep?: number;
  /** Quantization bucket size; higher = fewer buckets (e.g. 16/24/32) */
  quantizeStep?: number;
  /** Ignore pixels with alpha lower than this */
  minAlpha?: number;
  /** Ignore near-white pixels */
  ignoreNearWhite?: boolean;
  /** Ignore near-black pixels */
  ignoreNearBlack?: boolean;
};

export async function getProminentImageAccent(
  imageUrl: string,
  opts: ProminentImageColorOptions = {},
): Promise<ImageAccentResult> {
  const {
    maxWidth = 240,
    sampleStep = 6,
    quantizeStep = 24,
    minAlpha = 32,
    ignoreNearWhite = true,
    ignoreNearBlack = true,
  } = opts;

  const img = await loadImage(imageUrl);

  const scale = Math.min(1, maxWidth / Math.max(1, img.naturalWidth));
  const w = Math.max(1, Math.floor(img.naturalWidth * scale));
  const h = Math.max(1, Math.floor(img.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas not supported');

  ctx.drawImage(img, 0, 0, w, h);

  const { data } = ctx.getImageData(0, 0, w, h);

  // Histogram of quantized colors
  const buckets = new Map<string, { count: number; rgb: IRGB }>();

  for (let y = 0; y < h; y += sampleStep) {
    for (let x = 0; x < w; x += sampleStep) {
      const i = (y * w + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      if (a < minAlpha) continue;
      if (ignoreNearWhite && isNearWhite(r, g, b)) continue;
      if (ignoreNearBlack && isNearBlack(r, g, b)) continue;

      const qr = quantize(r, quantizeStep);
      const qg = quantize(g, quantizeStep);
      const qb = quantize(b, quantizeStep);

      const key = `${qr},${qg},${qb}`;
      const existing = buckets.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        buckets.set(key, { count: 1, rgb: { r: qr, g: qg, b: qb } });
      }
    }
  }

  // Fallback if we filtered everything out
  if (buckets.size === 0) {
    const accentRgb: IRGB = { r: 60, g: 60, b: 60 };
    const accentHex = IRGBToHEX(accentRgb);
    const accentHsl = RGBToHSL(accentRgb);

    return {
      accentRgb,
      accentHex,
      accentHsl,
      gradientCss: buildGradientCss(accentRgb),
    };
  }

  // Score top buckets: frequent + saturated + not too dark/light
  const top = [...buckets.values()].sort((a, b) => b.count - a.count).slice(0, 36);

  let best = top[0];
  let bestScore = -Infinity;

  for (const item of top) {
    const hsl = RGBToHSL(item.rgb); // uses your helper (IHSL with s/l in 0..100)
    const s = clamp01(hsl.s / 100);
    const l = clamp01(hsl.l / 100);

    const population = item.count;

    // Prefer “usable background” colors:
    // - boost saturation (vibrancy)
    // - penalize extremes of lightness
    // - penalize very gray colors
    const saturationBoost = 0.65 + s * 1.1;
    const grayPenalty = s < 0.12 ? 0.55 : 1.0;

    const lightPenalty = l < 0.1 ? 0.25 : l < 0.18 ? 0.6 : l > 0.92 ? 0.25 : l > 0.84 ? 0.6 : 1.0;

    const score = population * saturationBoost * grayPenalty * lightPenalty;

    if (score > bestScore) {
      bestScore = score;
      best = item;
    }
  }

  const accentRgb = clampRgb(best.rgb);
  const accentHex = IRGBToHEX(accentRgb);
  const accentHsl = RGBToHSL(accentRgb);

  return {
    accentRgb,
    accentHex,
    accentHsl,
    gradientCss: buildGradientCss(accentRgb),
  };
}

// ---------------- helpers ----------------

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // since you host on your own domain this should be safe; keep it anyway
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

function quantize(v: number, step: number): number {
  return Math.min(255, Math.max(0, Math.round(v / step) * step));
}

function isNearWhite(r: number, g: number, b: number): boolean {
  return r > 245 && g > 245 && b > 245;
}

function isNearBlack(r: number, g: number, b: number): boolean {
  return r < 10 && g < 10 && b < 10;
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

function clampRgb(rgb: IRGB): IRGB {
  return {
    r: Math.min(255, Math.max(0, Math.round(rgb.r))),
    g: Math.min(255, Math.max(0, Math.round(rgb.g))),
    b: Math.min(255, Math.max(0, Math.round(rgb.b))),
  };
}

/**
 * Simple “cover-style” background: accent -> darker accent.
 */
function buildGradientCss(accent: IRGB): string {
  const darker = darkenRgb(accent, 0.55);

  return `linear-gradient(180deg, rgb(${accent.r}, ${accent.g}, ${accent.b}) 0%, rgb(${darker.r}, ${darker.g}, ${darker.b}) 100%)`;
}

function darkenRgb(rgb: IRGB, amount: number): IRGB {
  const a = clamp01(amount);

  return clampRgb({
    r: rgb.r * (1 - a),
    g: rgb.g * (1 - a),
    b: rgb.b * (1 - a),
  });
}
